import React, { useEffect, useState } from "react";
import axios from "axios";

export default function AdminDashboard({ circleId, onNavigate }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError("");

    axios
      .get(
        process.env.REACT_APP_API_URL + `circles/admin/${circleId}/dashboard`,
        {
          headers: {
            Authorization: localStorage.getItem("jwtToken") || "",
          },
        },
      )
      .then(({ data }) => {
        if (!cancelled) setData(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.message || "Unable to load dashboard.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [circleId, revision]);

  const count = (value) =>
    typeof value === "number" ? value.toLocaleString() : "—";

  const cards = data
    ? [
        {
          label: "Members",
          value: data.stats.members,
          description: "Unblocked circle members",
          icon: "◎",
          color: "blue",
        },
        {
          label: "Visible activities",
          value: data.stats.activities,
          description: "Activities in your circle",
          icon: "↗",
          color: "teal",
        },
        {
          label: "Join requests",
          value: data.stats.pendingRequests,
          description: "Waiting for approval",
          icon: "＋",
          color: "amber",
        },
        {
          label: "Song requests",
          value: data.music?.pending,
          description: "Waiting for staff review",
          icon: "♫",
          color: "purple",
        },
      ]
    : [];

  return (
    <section className="ba-dashboard">
      <div className="ba-overview-heading">
        <div>
          <h3>Branch overview</h3>
          <p>Your community, at a glance.</p>
        </div>

        <button
          type="button"
          className="btn btn-outline-light"
          disabled={loading}
          onClick={() => setRevision((value) => value + 1)}
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {loading && !data && <p role="status">Loading branch overview…</p>}

      {data && (
        <>
          <div className="ba-overview-grid">
            {cards.map((card) => (
              <article
                key={card.label}
                className={`ba-metric ba-metric-${card.color}`}
              >
                <div className="ba-metric-top">
                  <span>{card.label}</span>
                  <span className="ba-metric-icon" aria-hidden="true">
                    {card.icon}
                  </span>
                </div>

                <strong className="ba-metric-value">{count(card.value)}</strong>

                <p>{card.description}</p>
              </article>
            ))}
          </div>

          <div className="ba-overview-bottom">
            <section className="ba-summary-card ba-music-summary">
              <div className="ba-summary-heading">
                <div>
                  <span className="ba-summary-eyebrow">MUSIC</span>
                  <h3>Your branch’s soundtrack</h3>
                </div>
                <span className="ba-summary-symbol" aria-hidden="true">
                  ♫
                </span>
              </div>

              <div className="ba-music-counts">
                {[
                  ["Pending", data.music?.pending, "amber"],
                  ["Queued", data.music?.queued, "purple"],
                  ["Played", data.music?.played, "teal"],
                ].map(([label, value, color]) => (
                  <div key={label} className={`ba-count-${color}`}>
                    <strong>{count(value)}</strong>
                    <span>{label}</span>
                  </div>
                ))}
              </div>

              <div className="ba-summary-footer">
                <span>Played count includes all recorded history.</span>

                {onNavigate && (
                  <button
                    type="button"
                    className="ba-add-song-link"
                    onClick={() => onNavigate("music")}
                  >
                    Manage music →
                  </button>
                )}
              </div>
            </section>

            <section className="ba-summary-card">
              <span className="ba-summary-eyebrow">SETTINGS</span>
              <h3>Branch status</h3>

              <div className="ba-status-row">
                <span>Leaderboard sharing</span>
                <span
                  className={`ba-status-pill ${
                    data.leaderboardEnabled ? "is-on" : ""
                  }`}
                >
                  {data.leaderboardEnabled ? "Enabled" : "Disabled"}
                </span>
              </div>

              <div className="ba-status-row">
                <span>Member song requests</span>
                <span
                  className={`ba-status-pill ${
                    data.musicRequestsEnabled ? "is-on" : ""
                  }`}
                >
                  {data.musicRequestsEnabled ? "Enabled" : "Disabled"}
                </span>
              </div>

              <div className="ba-status-row">
                <span>Member admins</span>
                <strong>{count(data.stats.admins)}</strong>
              </div>

              {onNavigate && (
                <button
                  type="button"
                  className="ba-add-song-link ba-settings-link"
                  onClick={() => onNavigate("leaderboard")}
                >
                  Leaderboard settings →
                </button>
              )}
            </section>
          </div>
        </>
      )}
    </section>
  );
}
